import { api_fetch, api_fetch_throws, HttpError, HttpResponse } from "@/api/client.tsx"
import { Result } from "@/utils"

/** One week of inbound receiving at a DC, with the rolling window ending that week. */
export interface FillRateWeek {
  week_start: string
  ordered: number
  received: number
  short: number
  shorted_value: number
  rolling_ordered: number
  rolling_received: number
  rolling_fill_rate: number | null
}

export interface DcFillRate {
  dc: string | null
  label: string
  weeks: FillRateWeek[]
  current_rolling_fill_rate: number | null
  below_threshold: boolean
  short_cases_in_window: number
  shorted_value_in_window: number
}

export type BrandResponse = "confirmed_short" | "shipped_in_full"

/** A PO line UNFI raised a receiving or short-dated alert on. */
export interface InboundAlert {
  id: string
  dc: string | null
  dc_label: string
  po_number: string
  item_number: string
  brand_sku: string | null
  description: string | null
  received_date: string | null
  quantity_ordered: number | null
  quantity_received: number | null
  quantity_short: number
  shorted_value: number | null
  is_shortage: boolean
  is_short_dated: boolean
  short_dated_cases: number | null
  expiry_date: string | null
  days_short_of_guarantee: number | null
  inventory_control_email: string | null
  brand_response: BrandResponse | null
  brand_response_note: string | null
  brand_response_at: string | null
}

export interface InboundFillRateResponse {
  threshold: number
  window_weeks: number
  dcs: DcFillRate[]
  alerts: InboundAlert[]
}

export async function fetchInboundFillRate(weeks: number): Promise<InboundFillRateResponse> {
  return await api_fetch_throws<InboundFillRateResponse>(`/inbound-po-line?weeks=${weeks}`)
}

export async function updateInboundPoLine(
  id: string,
  brand_response: BrandResponse | null,
  brand_response_note?: string
): Promise<Result<HttpResponse<unknown>, HttpError>> {
  return await api_fetch<unknown>(`/inbound-po-line/${id}`, {
    method: "PATCH",
    body: { brand_response, brand_response_note },
  })
}

/** One line of a Holland & Barrett PO as H&B received it. */
export interface DeliveryLine {
  id: string
  item_number: string
  brand_sku: string | null
  description: string | null
  quantity_ordered: number | null
  /** `null` while H&B is still receiving the line. */
  quantity_received: number | null
  short_units: number
  unit_cost: number | null
  received_date: string | null
  brand_response: BrandResponse | null
  brand_response_note: string | null
}

/** The payment that settled a PO. */
export interface DeliveryInvoice {
  deduction_id: string
  invoice_number: string
  invoice_date: string
  amount: number
  check_number: string | null
  check_date: string | null
  /** H&B paid only for what it received, not for the order. */
  paid_for_received_only: boolean
}

export interface DeliveryOrder {
  po_number: string
  expected_date: string | null
  last_received: string | null
  is_open: boolean
  days_late: number | null
  ordered_units: number
  received_units: number | null
  short_units: number
  ordered_value: number | null
  shorted_value: number
  /** The most H&B may charge for the late delivery (3% a week, 12% cap). */
  late_damages_cap: number | null
  invoice: DeliveryInvoice | null
  lines: DeliveryLine[]
}

export interface DeliveriesSummary {
  orders: number
  open: number
  on_time: number
  late: number
  in_full: number
  short: number
  on_time_rate: number | null
  in_full_rate: number | null
  otif_rate: number | null
  shorted_value: number
  late_damages_cap: number
}

export interface DeliveriesResponse {
  currency: string
  /** The brand's H&B supplier number (`HUK…`). */
  supplier_number: string | null
  summary: DeliveriesSummary
  orders: DeliveryOrder[]
}

/** Holland & Barrett POs as H&B received them, over the last `days` (open POs always included). */
export async function fetchDeliveries(days: number): Promise<DeliveriesResponse> {
  return await api_fetch_throws<DeliveriesResponse>(`/inbound-po-line/deliveries?days=${days}`)
}

/** One fined PO line beside UNFI's receiving record for it. */
export interface FineReviewLine {
  po_number: string
  item_number: string
  description: string | null
  received_date: string | null
  dc: string | null
  ordered: number
  received: number
  shorted_value: number | null
  fine: number
  alert_ordered: number | null
  alert_received: number | null
  has_receiving_alert: boolean
  brand_response: BrandResponse | null
  finding: string | null
}

/** A UNFI Canada `LOBLAWS FILL RATE` chargeback reviewed against the receiving record and Loblaw's policy. */
export interface FillRateFineReview {
  fine_total: number
  shorted_value_total: number
  charged_rate: number | null
  policy_rate: number
  policy_minimum_per_line: number
  amount_at_policy_rate: number
  lines: FineReviewLine[]
  contradicted_lines: number
  shipped_in_full_lines: number
  confirmed_short_lines: number
  rolling_fill_rate: number | null
  findings: string[]
  claim_message: string
}
